import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  Loader2,
  Check,
  Video,
  Image,
  FileText,
  Sparkles,
  Tag,
  Send,
  X,
  Play,
  Plus,
} from "lucide-react";
import ProductVideoCreator from "@/pages/ProductVideoCreator";
import UGC2SceneCreator from "@/pages/UGC2SceneCreator";
import UGCAvatar4Creator from "@/pages/UGCAvatar4Creator";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, title: "Setup", description: "Type & Aspect Ratio", icon: FileText },
  { id: 2, title: "Avatar", description: "Upload Avatar Image", icon: Image },
  { id: 3, title: "Background", description: "Upload Background Image", icon: Image },
  { id: 4, title: "Product", description: "Upload Sample Product", icon: Image },
  { id: 5, title: "Composite", description: "Generate Avatar + Product", icon: Sparkles },
  { id: 6, title: "Script", description: "Write the Script", icon: FileText },
  { id: 7, title: "Video", description: "Generate Sample Video", icon: Video },
  { id: 8, title: "Details", description: "Tags & Description", icon: Tag },
  { id: 9, title: "Publish", description: "Review & Publish", icon: Send },
];

const ASPECT_RATIOS = [
  { value: "9:16", label: "9:16 (Portrait)", description: "TikTok, Reels, Shorts" },
  { value: "16:9", label: "16:9 (Landscape)", description: "YouTube, Ads" },
  { value: "1:1", label: "1:1 (Square)", description: "Instagram, Facebook" },
];

const TEMPLATE_TYPES = [
  { value: "ugc", label: "UGC Video", description: "User-generated content style" },
  { value: "ugc_sora2", label: "UGC Sora 2", description: "Single-scene UGC with variable duration" },
  { value: "product_video", label: "Product Video", description: "Multi-scene product showcase" },
  { value: "hero_shot", label: "Hero Shot", description: "Single-scene product video via Veo 3.1" },
  { value: "ugc_2scene", label: "UGC 2-Scene", description: "Two-scene avatar video" },
  { value: "ugc_avatar4", label: "UGC Avatar4", description: "HeyGen Avatar4 with voice selection" },
];

export default function TemplateCreator() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const isEditMode = !!templateId;
  const { isAdmin } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [template, setTemplate] = useState(null);
  
  // Form states
  const [templateType, setTemplateType] = useState("ugc");
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [sceneCount, setSceneCount] = useState(2);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [productFile, setProductFile] = useState(null);
  const [productPreview, setProductPreview] = useState(null);
  const [script, setScript] = useState("");
  const [i2vPrompt, setI2vPrompt] = useState("");
  const [actionPrompt, setActionPrompt] = useState("");
  const [compositePrompt, setCompositePrompt] = useState("");
  const [compositeSeed, setCompositeSeed] = useState("");
  const [compositeMethod, setCompositeMethod] = useState("gemini");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [creditsCost, setCreditsCost] = useState("");
  const [productType, setProductType] = useState("");
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  
  // Job states
  const [compositeJobId, setCompositeJobId] = useState(null);
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoMethod, setVideoMethod] = useState("fal_veo");
  const [videoDuration, setVideoDuration] = useState(8);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/projects");
      return;
    }
    if (isEditMode) {
      loadTemplate();
    }
  }, [isAdmin, navigate, templateId]);

  const loadTemplate = async () => {
    setInitialLoading(true);
    try {
      const response = await axios.get(`${API}/api/templates/${templateId}`, {
        withCredentials: true,
      });
      const t = response.data;
      setTemplate(t);
      setTemplateType(t.template_type || "ugc");
      setAspectRatio(t.aspect_ratio || "9:16");
      setSceneCount(t.scene_count || 2);
      setScript(t.script || "");
      setI2vPrompt(t.i2v_prompt || "");
      setActionPrompt(t.action_prompt || "");
      setCompositePrompt(t.composite_prompt || "");
      setCompositeSeed(t.composite_seed != null ? String(t.composite_seed) : "");
      setCompositeMethod(t.composite_method || "gemini");
      setVideoMethod(t.video_method || "fal_veo");
      setVideoDuration(t.video_duration || 8);
      setTitle(t.title || "");
      setDescription(t.description || "");
      setCreditsCost(t.credits_cost != null ? String(t.credits_cost) : "");
      setProductType(t.product_type || "");
      setTags(t.tags || []);
      if (t.avatar_image) setAvatarPreview(t.avatar_image);
      if (t.background_image) setBackgroundPreview(t.background_image);
      if (t.sample_product_image) setProductPreview(t.sample_product_image);
      // Determine which step to start on based on progress
      if (!t.avatar_image) setCurrentStep(2);
      else if (!t.background_image) setCurrentStep(3);
      else if (!t.sample_product_image) setCurrentStep(4);
      else if (!t.composite_image) setCurrentStep(5);
      else if (!t.script) setCurrentStep(6);
      else if (!t.sample_video) setCurrentStep(7);
      else if (!t.title) setCurrentStep(8);
      else setCurrentStep(9);
    } catch (error) {
      console.error("Error loading template:", error);
      toast.error("Failed to load template");
      navigate("/admin/templates");
    } finally {
      setInitialLoading(false);
    }
  };

  // Step 1: Create template
  const createTemplate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/templates`,
        { template_type: templateType, aspect_ratio: aspectRatio, scene_count: templateType === "product_video" ? sceneCount : null, video_duration: templateType === "ugc_sora2" ? videoDuration : null },
        { withCredentials: true }
      );
      setTemplate(response.data.template);
      toast.success("Template created!");
      setCurrentStep(2);
    } catch (error) {
      console.error("Error creating template:", error);
      toast.error(error.response?.data?.detail || "Failed to create template");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Upload avatar
  const uploadAvatar = async () => {
    if (!avatarFile || !template) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", avatarFile);
      
      const response = await axios.post(
        `${API}/api/templates/${template.id}/upload-avatar`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      setTemplate({ ...template, avatar_image: response.data.avatar_image });
      toast.success("Avatar uploaded!");
      setCurrentStep(3);
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error(error.response?.data?.detail || "Failed to upload avatar");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Upload background
  const uploadBackground = async () => {
    if (!template) return;
    
    // If no new file selected but background already exists, just advance
    if (!backgroundFile && template.background_image) {
      setCurrentStep(4);
      return;
    }
    
    if (!backgroundFile) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", backgroundFile);
      
      const response = await axios.post(
        `${API}/api/templates/${template.id}/upload-background`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      setTemplate({ ...template, background_image: response.data.background_image });
      toast.success("Background uploaded!");
      setCurrentStep(4);
    } catch (error) {
      console.error("Error uploading background:", error);
      toast.error(error.response?.data?.detail || "Failed to upload background");
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Upload product
  const uploadProduct = async () => {
    if (!productFile || !template) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", productFile);
      
      const response = await axios.post(
        `${API}/api/templates/${template.id}/upload-sample-product`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      setTemplate({ ...template, sample_product_image: response.data.sample_product_image });
      toast.success("Product image uploaded!");
      setCurrentStep(5);
    } catch (error) {
      console.error("Error uploading product:", error);
      toast.error(error.response?.data?.detail || "Failed to upload product");
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Generate composite
  const generateComposite = async () => {
    if (!template) return;
    if (!compositePrompt.trim()) {
      toast.error("Please enter a prompt for composite image generation");
      return;
    }
    
    setLoading(true);
    try {
      const label = compositeMethod === "gemini" ? "Gemini 3 Pro" : "Nano Banana Pro";
      toast.info(`Generating composite image via ${label}... This may take a minute.`);
      
      const formData = new FormData();
      formData.append("prompt", compositePrompt);
      formData.append("method", compositeMethod);
      if (compositeMethod === "nano_banana" && compositeSeed.trim()) {
        formData.append("seed", parseInt(compositeSeed, 10));
      }
      
      const response = await axios.post(
        `${API}/api/templates/${template.id}/generate-composite`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      if (response.data.seed) setCompositeSeed(String(response.data.seed));
      
      // Poll for completion
      const jobId = response.data.job_id;
      const pollInterval = setInterval(async () => {
        try {
          const jobResp = await axios.get(
            `${API}/api/templates/${template.id}/composite-job/${jobId}`,
            { withCredentials: true }
          );
          const job = jobResp.data;
          if (job.status === "completed") {
            clearInterval(pollInterval);
            const tResp = await axios.get(`${API}/api/templates/${template.id}`, { withCredentials: true });
            const t = tResp.data.template || tResp.data;
            if (t.composite_image) {
              t.composite_image = `${t.composite_image}?t=${Date.now()}`;
            }
            setTemplate(t);
            setLoading(false);
            toast.success("Composite image generated!");
          } else if (job.status === "failed") {
            clearInterval(pollInterval);
            setLoading(false);
            toast.error(job.message || "Failed to generate composite");
          }
        } catch {
          clearInterval(pollInterval);
          setLoading(false);
        }
      }, 5000);
    } catch (error) {
      console.error("Error generating composite:", error);
      toast.error(error.response?.data?.detail || "Failed to generate composite");
      setLoading(false);
    }
  };

  // Step 5: Save script and action prompt, build i2v prompt
  const saveScriptAndActionPrompt = async () => {
    if (!template || !script.trim()) {
      toast.error("Please enter a script");
      return;
    }
    if (!actionPrompt.trim()) {
      toast.error("Please enter an action prompt");
      return;
    }
    
    setLoading(true);
    try {
      // Save script first
      const formData = new FormData();
      formData.append("script", script);
      
      await axios.post(
        `${API}/api/templates/${template.id}/save-script`,
        formData,
        { withCredentials: true }
      );
      
      // Save action_prompt via PUT
      await axios.put(
        `${API}/api/templates/${template.id}`,
        { action_prompt: actionPrompt },
        { withCredentials: true }
      );
      
      // Build i2v prompt locally: action + script
      const builtPrompt = `${actionPrompt}\n\nScript: "${script}"`;
      
      // Save the built i2v prompt
      await axios.put(
        `${API}/api/templates/${template.id}`,
        { i2v_prompt: builtPrompt },
        { withCredentials: true }
      );
      
      setI2vPrompt(builtPrompt);
      setTemplate({ ...template, script, action_prompt: actionPrompt, i2v_prompt: builtPrompt });
      toast.success("Script & action prompt saved! Review the final prompt below.");
    } catch (error) {
      console.error("Error saving script:", error);
      toast.error(error.response?.data?.detail || "Failed to save script");
    } finally {
      setLoading(false);
    }
  };

  // Save edited i2v prompt and advance to video step
  const savePromptAndContinue = async () => {
    if (!template || !i2vPrompt.trim()) {
      toast.error("Prompt cannot be empty");
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/api/templates/${template.id}`,
        { i2v_prompt: i2vPrompt },
        { withCredentials: true }
      );
      setTemplate({ ...template, i2v_prompt: i2vPrompt });
      toast.success("Prompt saved!");
      setCurrentStep(7);
    } catch (error) {
      console.error("Error saving prompt:", error);
      toast.error("Failed to save prompt");
    } finally {
      setLoading(false);
    }
  };

  // Step 6: Generate video
  const generateVideo = async () => {
    if (!template) return;
    
    setLoading(true);
    try {
      const labels = {"gemini_veo": "Gemini Veo 3.1 Fast", "fal_sora2": "Sora 2", "fal_flf": "Veo FLF", "fal_veo": "Fal AI Veo"};
      const label = labels[videoMethod] || "Fal AI Veo Wrapper";
      toast.info(`Starting video generation via ${label}... This may take a few minutes.`);
      
      const response = await axios.post(
        `${API}/api/templates/${template.id}/generate-sample-video`,
        { video_method: videoMethod },
        { withCredentials: true }
      );
      
      setVideoJobId(response.data.job_id);
      pollVideoStatus(response.data.job_id);
    } catch (error) {
      console.error("Error starting video generation:", error);
      toast.error(error.response?.data?.detail || "Failed to start video generation");
      setLoading(false);
    }
  };

  const pollVideoStatus = async (jobId) => {
    let attempts = 0;
    const maxAttempts = 120;
    
    while (attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      
      try {
        const response = await axios.get(
          `${API}/api/templates/${template.id}/video-job-status?t=${Date.now()}`,
          {
            withCredentials: true,
            headers: { "Cache-Control": "no-cache" },
          }
        );
        
        const job = response.data;
        
        if (job.status === "completed") {
          const videoUrl = job.result?.video_url;
          setTemplate({ ...template, sample_video: videoUrl ? `${videoUrl}?t=${Date.now()}` : videoUrl });
          toast.success("Video generated!");
          setLoading(false);
          setCurrentStep(8);
          return;
        } else if (job.status === "failed") {
          toast.error(job.error || "Video generation failed");
          setLoading(false);
          return;
        }
        
        console.log(`Video generation: ${job.progress}% - ${job.message}`);
      } catch (error) {
        console.error("Error polling video status:", error);
      }
      
      attempts++;
    }
    
    toast.error("Video generation timed out");
    setLoading(false);
  };

  // Step 7: Save details
  const saveDetails = async () => {
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    if (tags.length === 0) {
      toast.error("Please add at least one tag");
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(
        `${API}/api/templates/${template.id}`,
        { title, description, tags, credits_cost: creditsCost ? parseFloat(creditsCost) : null, product_type: productType.trim() || null },
        { withCredentials: true }
      );
      
      setTemplate({ ...template, title, description, tags, credits_cost: creditsCost ? parseFloat(creditsCost) : null, product_type: productType.trim() || null });
      toast.success("Details saved!");
      setCurrentStep(9);
    } catch (error) {
      console.error("Error saving details:", error);
      toast.error(error.response?.data?.detail || "Failed to save details");
    } finally {
      setLoading(false);
    }
  };

  // Step 8: Publish
  const publishTemplate = async () => {
    if (!template) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/templates/${template.id}/publish`,
        { title, description, tags },
        { withCredentials: true }
      );
      
      toast.success("Template published!");
      navigate("/admin/templates");
    } catch (error) {
      console.error("Error publishing template:", error);
      toast.error(error.response?.data?.detail || "Failed to publish template");
    } finally {
      setLoading(false);
    }
  };

  // File handlers
  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleBackgroundChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBackgroundFile(file);
      setBackgroundPreview(URL.createObjectURL(file));
    }
  };

  const handleProductChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProductFile(file);
      setProductPreview(URL.createObjectURL(file));
    }
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Template Type</Label>
              <p className="text-sm text-muted-foreground mb-3">Select the type of video template</p>
              <div className="grid grid-cols-1 gap-3">
                {TEMPLATE_TYPES.map((type) => (
                  <div
                    key={type.value}
                    onClick={() => {
                      setTemplateType(type.value);
                      if (type.value === "ugc_sora2") setVideoMethod("fal_sora2");
                    }}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      templateType === type.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="font-medium">{type.label}</div>
                    <div className="text-sm text-muted-foreground">{type.description}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <Label className="text-base font-medium">Aspect Ratio</Label>
              <p className="text-sm text-muted-foreground mb-3">Choose the video dimensions</p>
              <div className="grid grid-cols-3 gap-3">
                {ASPECT_RATIOS.map((ratio) => (
                  <div
                    key={ratio.value}
                    onClick={() => setAspectRatio(ratio.value)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                      aspectRatio === ratio.value
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="font-medium">{ratio.label}</div>
                    <div className="text-xs text-muted-foreground">{ratio.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Scene count for product_video */}
            {templateType === "product_video" && (
              <div>
                <Label className="text-base font-medium">Number of Scenes</Label>
                <p className="text-sm text-muted-foreground mb-3">How many scenes in this product video?</p>
                <div className="grid grid-cols-3 gap-3">
                  {[2, 3, 4].map((n) => (
                    <div
                      key={n}
                      onClick={() => setSceneCount(n)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                        sceneCount === n
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                      data-testid={`scene-count-${n}`}
                    >
                      <div className="font-medium text-lg">{n}</div>
                      <div className="text-xs text-muted-foreground">Scenes</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Duration selector for ugc_sora2 */}
            {templateType === "ugc_sora2" && (
              <div>
                <Label className="text-base font-medium">Video Duration</Label>
                <p className="text-sm text-muted-foreground mb-3">Select video length — generated via Sora 2</p>
                <div className="grid grid-cols-5 gap-3">
                  {[6, 8, 12, 16, 20].map((d) => (
                    <div
                      key={d}
                      onClick={() => setVideoDuration(d)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                        videoDuration === d
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                      data-testid={`duration-${d}`}
                    >
                      <div className="font-medium text-lg">{d}s</div>
                      <div className="text-xs text-muted-foreground">seconds</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <Button onClick={isEditMode && template ? () => setCurrentStep(2) : createTemplate} disabled={loading} className="w-full" data-testid="step1-continue-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isEditMode && template ? "Continue" : "Create Template & Continue"}
            </Button>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Avatar Image</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Upload the avatar/person image that will hold the product
              </p>
              
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:border-primary/50 ${
                  avatarPreview ? "border-primary bg-primary/5" : "border-border"
                }`}
                onClick={() => document.getElementById("avatar-upload").click()}
              >
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar preview"
                    className="max-h-64 mx-auto rounded-lg"
                  />
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-10 h-10 mx-auto text-muted-foreground" />
                    <p className="text-muted-foreground">Click to upload avatar image</p>
                    <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB</p>
                  </div>
                )}
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </div>
            </div>
            
            <Button onClick={uploadAvatar} disabled={loading || !avatarFile} className="w-full">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Upload & Continue
            </Button>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Background Image</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Upload the background/environment image for the scene (e.g., coffee shop, studio, outdoor)
              </p>
              
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:border-primary/50 ${
                  backgroundPreview ? "border-primary bg-primary/5" : "border-border"
                }`}
                onClick={() => document.getElementById("background-upload").click()}
                data-testid="background-upload-zone"
              >
                {backgroundPreview ? (
                  <img
                    src={backgroundPreview}
                    alt="Background preview"
                    className="max-h-64 mx-auto rounded-lg"
                  />
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-10 h-10 mx-auto text-muted-foreground" />
                    <p className="text-muted-foreground">Click to upload background image</p>
                    <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB</p>
                  </div>
                )}
                <input
                  id="background-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleBackgroundChange}
                  data-testid="background-file-input"
                />
              </div>
            </div>
            
            <Button onClick={uploadBackground} disabled={loading || (!backgroundFile && !template?.background_image)} className="w-full" data-testid="upload-background-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {template?.background_image && !backgroundFile ? "Continue" : "Upload & Continue"}
            </Button>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Sample Product Image</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Upload a sample product image for the template preview
              </p>
              
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all hover:border-primary/50 ${
                  productPreview ? "border-primary bg-primary/5" : "border-border"
                }`}
                onClick={() => document.getElementById("product-upload").click()}
              >
                {productPreview ? (
                  <img
                    src={productPreview}
                    alt="Product preview"
                    className="max-h-64 mx-auto rounded-lg"
                  />
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-10 h-10 mx-auto text-muted-foreground" />
                    <p className="text-muted-foreground">Click to upload product image</p>
                    <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB</p>
                  </div>
                )}
                <input
                  id="product-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleProductChange}
                />
              </div>
            </div>
            
            <Button onClick={uploadProduct} disabled={loading || !productFile} className="w-full">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Upload & Continue
            </Button>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Generate Composite Image</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Choose a generation method, write your prompt, and generate the composite.
              </p>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <p className="text-sm font-medium mb-2">Avatar</p>
                  {template?.avatar_image && (
                    <img
                      src={template.avatar_image}
                      alt="Avatar"
                      className="max-h-48 mx-auto rounded-lg border"
                      data-testid="composite-avatar-preview"
                    />
                  )}
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium mb-2">Background</p>
                  {template?.background_image && (
                    <img
                      src={template.background_image}
                      alt="Background"
                      className="max-h-48 mx-auto rounded-lg border"
                      data-testid="composite-background-preview"
                    />
                  )}
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium mb-2">Product</p>
                  {template?.sample_product_image && (
                    <img
                      src={template.sample_product_image}
                      alt="Product"
                      className="max-h-48 mx-auto rounded-lg border"
                      data-testid="composite-product-preview"
                    />
                  )}
                </div>
              </div>

              {/* Generation Method Selector */}
              <div className="mb-4">
                <Label className="text-sm font-medium">Generation Method</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  This method will also be used when users recreate from this template.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    onClick={() => setCompositeMethod("nano_banana")}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      compositeMethod === "nano_banana"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="method-nano-banana"
                  >
                    <div className="font-medium text-sm">Fal AI (Nano Banana)</div>
                    <div className="text-xs text-muted-foreground mt-1">Seed-based reproducibility</div>
                  </div>
                  <div
                    onClick={() => setCompositeMethod("gemini")}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      compositeMethod === "gemini"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="method-gemini"
                  >
                    <div className="font-medium text-sm">Gemini 3 Pro Preview</div>
                    <div className="text-xs text-muted-foreground mt-1">Advanced text overlay support</div>
                  </div>
                </div>
              </div>

              {/* Prompt */}
              <div className="mb-4">
                <Label className="text-sm font-medium">Image Generation Prompt</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Describe how the avatar should hold/use the product. This same prompt will be reused when users recreate from this template.
                </p>
                <Textarea
                  value={compositePrompt}
                  onChange={(e) => setCompositePrompt(e.target.value)}
                  placeholder="e.g., Create a photorealistic UGC-style photo of the person naturally holding the product, looking at the camera with a genuine smile. Casual setting, natural lighting."
                  className="min-h-[100px]"
                  data-testid="composite-prompt-textarea"
                />
              </div>

              {/* Seed - only for Nano Banana */}
              {compositeMethod === "nano_banana" && (
                <div className="mb-4">
                  <Label className="text-sm font-medium">Seed (optional)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Set a seed for reproducible results. Same seed + prompt = consistent output across recreations.
                  </p>
                  <Input
                    type="number"
                    value={compositeSeed}
                    onChange={(e) => setCompositeSeed(e.target.value)}
                    placeholder="e.g., 12345 (auto-generated if empty)"
                    data-testid="composite-seed-input"
                  />
                </div>
              )}
              
              {template?.composite_image && (
                <div className="mb-6 p-4 bg-muted/30 rounded-xl border">
                  <p className="text-sm font-semibold mb-3 text-center">Generated Composite — Review</p>
                  <div className="flex justify-center">
                    <img
                      src={template.composite_image}
                      alt="Composite"
                      className="max-h-[520px] w-auto rounded-lg border shadow-md"
                      data-testid="composite-image-large-preview"
                    />
                  </div>
                </div>
              )}
            </div>
            
            <Button onClick={generateComposite} disabled={loading || !compositePrompt.trim()} className="w-full" data-testid="generate-composite-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : template?.composite_image ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Regenerate Composite
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Composite Image
                </>
              )}
            </Button>
            
            {template?.composite_image && (
              <Button onClick={() => setCurrentStep(6)} variant="outline" className="w-full" data-testid="composite-continue-btn">
                Continue with this image <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        );

      case 6:
        // For ugc_avatar4, show the Avatar4 creator (voice + script + video gen)
        if (templateType === "ugc_avatar4") {
          return <UGCAvatar4Creator template={template} setTemplate={setTemplate} onContinue={() => setCurrentStep(7)} />;
        }
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Action Prompt</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Describe the action/movement for the video. This is the static part that will be reused for all recreations.
              </p>
              
              <Textarea
                value={actionPrompt}
                onChange={(e) => setActionPrompt(e.target.value)}
                placeholder="Example: 'Make the person in the image speak naturally with subtle head movements and authentic facial expressions. The avatar's appearance, clothing, and background must remain exactly as in the source image.'"
                className="min-h-[120px]"
                data-testid="action-prompt-textarea"
              />
            </div>

            <div>
              <Label className="text-base font-medium">Script</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Write the script for the avatar to speak (6-7 seconds, ~20-30 words)
              </p>
              
              <Textarea
                value={script}
                onChange={(e) => setScript(e.target.value)}
                placeholder="Example: 'OMG you guys! This product literally changed my life. I use it every single day and the results are insane. You NEED to try this!'"
                className="min-h-[120px]"
                data-testid="script-textarea"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {script.split(" ").filter((w) => w).length}{templateType === "ugc_sora2" ? `/${videoDuration * 3}` : ""} words
                {templateType === "ugc_sora2" && <span className="ml-1">({videoDuration}s @ 3 wps)</span>}
              </p>
            </div>
            
            {template?.composite_image && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">Preview with composite</p>
                <img
                  src={template.composite_image}
                  alt="Composite"
                  className="max-h-40 mx-auto rounded-lg"
                />
              </div>
            )}
            
            <Button
              onClick={saveScriptAndActionPrompt}
              disabled={loading || !script.trim() || !actionPrompt.trim()}
              className="w-full"
              data-testid="save-script-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {i2vPrompt ? "Update & Rebuild Prompt" : "Save & Build Video Prompt"}
            </Button>

            {/* Combined prompt preview — shown after save */}
            {i2vPrompt && (
              <div className="space-y-3 pt-2 border-t border-border">
                <div>
                  <Label className="text-base font-medium">Final Video Prompt — Review & Edit</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    This is Action + Script combined. This will be sent to the video generation model. Edit if needed.
                  </p>
                  <Textarea
                    value={i2vPrompt}
                    onChange={(e) => setI2vPrompt(e.target.value)}
                    className="min-h-[160px] font-mono text-sm"
                    data-testid="i2v-prompt-textarea"
                  />
                </div>
                
                <Button
                  onClick={savePromptAndContinue}
                  disabled={loading || !i2vPrompt.trim()}
                  className="w-full"
                  data-testid="save-prompt-continue-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  Save Prompt & Continue to Video
                </Button>
              </div>
            )}
          </div>
        );

      case 7:
        // For ugc_avatar4, show video review with continue button
        if (templateType === "ugc_avatar4") {
          const avatar4Video = template?.scene_videos?.[0];
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-base font-medium">Sample Video Review</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Review the generated Avatar4 video before proceeding.
                </p>
              </div>

              {avatar4Video ? (
                <div className="space-y-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Generated Avatar4 video</p>
                    <video src={avatar4Video} controls className="w-full max-h-80 mx-auto rounded-lg" />
                  </div>
                  <Button onClick={() => setCurrentStep(8)} className="w-full" data-testid="avatar4-video-continue-btn">
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Video className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No video generated yet. Go back to Step 6 to generate a video.</p>
                  <Button variant="outline" className="mt-3" onClick={() => setCurrentStep(6)}>
                    Back to Script & Video
                  </Button>
                </div>
              )}
            </div>
          );
        }
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Generate Sample Video</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Choose a generation method and generate the video using the composite image and your finalized prompt
              </p>

              {/* Video Method Selector - hidden for ugc_sora2 (locked to Sora 2) */}
              {templateType === "ugc_sora2" ? (
                <div className="mb-5 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-medium text-sm">Sora 2</div>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Locked</span>
                  </div>
                  <div className="text-xs text-muted-foreground">OpenAI Sora 2 via Fal.ai — Duration: {videoDuration}s</div>
                </div>
              ) : (
              <div className="mb-5">
                <Label className="text-sm font-medium">Video Generation Method</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  This method will also be used when users recreate from this template.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    onClick={() => setVideoMethod("fal_veo")}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      videoMethod === "fal_veo"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="video-method-fal"
                  >
                    <div className="font-medium text-sm">Fal AI (Veo Wrapper)</div>
                    <div className="text-xs text-muted-foreground mt-1">Current workflow via Fal.ai</div>
                  </div>
                  <div
                    onClick={() => setVideoMethod("gemini_veo")}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      videoMethod === "gemini_veo"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="video-method-gemini"
                  >
                    <div className="font-medium text-sm">Gemini Veo 3.1 Fast</div>
                    <div className="text-xs text-muted-foreground mt-1">Direct Google API, native audio</div>
                  </div>
                  <div
                    onClick={() => setVideoMethod("fal_flf")}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      videoMethod === "fal_flf"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="video-method-fal-flf"
                  >
                    <div className="font-medium text-sm">Veo 3.1 Fast — First & Last Frame</div>
                    <div className="text-xs text-muted-foreground mt-1">Uses composite as both first and last frame for loopable video</div>
                  </div>
                  <div
                    onClick={() => setVideoMethod("fal_sora2")}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      videoMethod === "fal_sora2"
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                    data-testid="video-method-sora2"
                  >
                    <div className="font-medium text-sm">Sora 2</div>
                    <div className="text-xs text-muted-foreground mt-1">OpenAI Sora 2 via Fal.ai — high quality image-to-video</div>
                  </div>
                </div>
              </div>
              )}
              
              {/* Show finalized prompt as read-only summary */}
              {i2vPrompt && (
                <div className="p-4 bg-muted/50 rounded-lg mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">Finalized i2v Prompt</p>
                    <button
                      onClick={() => setCurrentStep(6)}
                      className="text-xs text-primary hover:underline"
                      data-testid="edit-prompt-link"
                    >
                      Edit
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{i2vPrompt}</p>
                </div>
              )}
              
              {template?.sample_video && (
                <div className="mb-6">
                  <p className="text-sm font-medium mb-2 text-center">Generated Video</p>
                  <video
                    src={template.sample_video}
                    controls
                    className="max-h-80 mx-auto rounded-lg"
                    data-testid="sample-video-player"
                  />
                </div>
              )}
            </div>
            
            <Button onClick={generateVideo} disabled={loading} className="w-full" data-testid="generate-video-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Video...
                </>
              ) : template?.sample_video ? (
                <>
                  <Video className="w-4 h-4 mr-2" />
                  Regenerate Video
                </>
              ) : (
                <>
                  <Video className="w-4 h-4 mr-2" />
                  Generate Sample Video
                </>
              )}
            </Button>
            
            {template?.sample_video && (
              <Button onClick={() => setCurrentStep(8)} variant="outline" className="w-full" data-testid="video-continue-btn">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <div>
              <Label className="text-base font-medium">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Latina Girl App Demo"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-base font-medium">Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this template is best used for..."
                className="mt-1 min-h-[80px]"
              />
            </div>

            <div>
              <Label className="text-base font-medium">Recreation Credit Cost</Label>
              <p className="text-sm text-muted-foreground mb-1">Credits deducted when a user recreates this template. Leave blank to use the default.</p>
              <Input
                type="number"
                value={creditsCost}
                onChange={(e) => setCreditsCost(e.target.value)}
                placeholder="e.g., 3"
                className="mt-1 w-40"
                min="0"
                step="0.5"
                data-testid="template-credits-cost-input"
              />
            </div>
            
            <div>
              <Label className="text-base font-medium">Product Type</Label>
              <p className="text-sm text-muted-foreground mb-1">Internal label for this product category (not shown to users)</p>
              <Input
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                placeholder="e.g., Skincare, Electronics, Apparel"
                className="mt-1 w-64"
                data-testid="template-product-type-input"
              />
            </div>

            <div>
              <Label className="text-base font-medium">Tags</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Add tags to help users find this template
              </p>
              
              <div className="flex gap-2 mb-3">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag..."
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                />
                <Button type="button" onClick={addTag} variant="outline">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="px-3 py-1">
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      className="ml-2 hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              
              {/* Suggested tags */}
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-2">Suggested:</p>
                <div className="flex flex-wrap gap-1">
                  {["UGC", "Apps", "Beauty", "Fashion", "Tech", "Food", "Health", "Lifestyle"].map(
                    (suggestion) =>
                      !tags.includes(suggestion) && (
                        <button
                          key={suggestion}
                          onClick={() => setTags([...tags, suggestion])}
                          className="text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80"
                        >
                          + {suggestion}
                        </button>
                      )
                  )}
                </div>
              </div>
            </div>
            
            <Button onClick={saveDetails} disabled={loading} className="w-full">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save & Continue to Review
            </Button>
          </div>
        );

      case 9:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold">Review Your Template</h3>
              <p className="text-sm text-muted-foreground">
                Make sure everything looks good before publishing
              </p>
            </div>
            
            {/* Preview Card */}
            <Card>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    {template?.sample_video ? (
                      <video
                        src={template.sample_video}
                        controls
                        className="w-full rounded-lg"
                      />
                    ) : template?.composite_image ? (
                      <img
                        src={template.composite_image}
                        alt="Preview"
                        className="w-full rounded-lg"
                      />
                    ) : null}
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-xl font-bold">{title || "Untitled"}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {description || "No description"}
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <Badge key={tag} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="text-sm">
                      <p>
                        <span className="text-muted-foreground">Type:</span>{" "}
                        {templateType.toUpperCase()}
                      </p>
                      <p>
                        <span className="text-muted-foreground">Aspect Ratio:</span>{" "}
                        {aspectRatio}
                      </p>
                    </div>
                    
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm font-medium mb-1">Script</p>
                      <p className="text-sm text-muted-foreground">"{script}"</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex gap-3">
              <Button onClick={() => setCurrentStep(8)} variant="outline" className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Edit Details
              </Button>
              <Button onClick={publishTemplate} disabled={loading} className="flex-1">
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Publish Template
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin/templates")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="h-6 w-px bg-border" />
              <h1 className="text-lg font-semibold">{isEditMode ? "Edit Template" : "Create Template"}</h1>
            </div>
            
            {template && (
              <Badge variant="outline">
                Draft: {template.id.slice(0, 8)}...
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Progress Steps - only for UGC flow */}
      {(templateType !== "product_video" && templateType !== "ugc_2scene" || !template) && (
        <div className="border-b border-border bg-white">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              {STEPS.map((step, index) => {
                const Icon = step.icon;
                const isCompleted = currentStep > step.id;
                const isCurrent = currentStep === step.id;
                
                return (
                  <React.Fragment key={step.id}>
                    <div
                      className={`flex flex-col items-center ${
                        isEditMode && template ? "cursor-pointer" : ""
                      } ${
                        isCurrent ? "text-primary" : isCompleted ? "text-green-600" : "text-muted-foreground"
                      }`}
                      onClick={() => {
                        if (isEditMode && template) setCurrentStep(step.id);
                      }}
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isCurrent
                            ? "bg-primary text-white"
                            : isCompleted
                            ? "bg-green-100 text-green-600"
                            : "bg-muted"
                        }`}
                      >
                        {isCompleted ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                      </div>
                      <span className="text-xs mt-1 hidden sm:block">{step.title}</span>
                    </div>
                    
                    {index < STEPS.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 ${
                          currentStep > step.id ? "bg-green-500" : "bg-muted"
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-8">
        {initialLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (templateType === "product_video" || templateType === "hero_shot") && template ? (
          <ProductVideoCreator template={template} setTemplate={setTemplate} />
        ) : templateType === "ugc_2scene" && template ? (
          <UGC2SceneCreator template={template} setTemplate={setTemplate} />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{STEPS[currentStep - 1]?.title}</CardTitle>
              <CardDescription>{STEPS[currentStep - 1]?.description}</CardDescription>
            </CardHeader>
            <CardContent>{renderStepContent()}</CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
